import { useCallback, useState } from 'react';

import { getConfig } from '@/modules/runtimeConfig';

import { LOGIN_ROUTES } from '../constants';
import { redirectToUrl } from '../utils/redirectToUrl';

export type UseSignIn = {
  signInOpen: boolean;
  setSignInOpen: (open: boolean) => void;
  onSignIn: () => void;
};

// The canonical login-app URL for this deploy. Staging is the default for any
// non-production build (and for local dev — though dev shows the SignInBottomSheet
// instead of redirecting at all).
//
// Resolved on each call rather than once at module scope: runtime config is published
// during `OnboardingRoot`'s render, so a module-scope read would freeze this to the env
// fallback and send a consumer's production users to staging login.
const getLoginUrl = () =>
  getConfig('vidaEnv') === 'production' ? LOGIN_ROUTES.production : LOGIN_ROUTES.staging;

// Sign-in entry point shared by the flows that surface it (normal + PURL).
// Local dev (Expo, __DEV__ === true) shows the bottom-sheet sign-in flow because
// there's no separate login app running on localhost to redirect to. Deployed
// builds hand off to the canonical login app at staging-app / app.vida.com.
//
// `initialOpen` seeds the sheet already-open — used by the PURL already-enrolled
// dev backdrop, where signing in is the intended next step.
export function useSignIn(initialOpen = false): UseSignIn {
  const [signInOpen, setSignInOpen] = useState(initialOpen);

  const onSignIn = useCallback(() => {
    if (__DEV__) {
      setSignInOpen(true);
      return;
    }
    // Same-tab navigation (not a new tab) so the user leaves onboarding for login.
    redirectToUrl(getLoginUrl());
  }, []);

  return { signInOpen, setSignInOpen, onSignIn };
}
